import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useStore, useDispatch, useSelector } from "react-redux";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import {
  EditDialog,
  type EditDialogProps,
} from "src/pages/TransactionDetails/Expenses/EditDialog";
import { activeDialogPropsSelector } from "src/store/adminHtml/selector";
import { transactionByIdSelector } from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import ConfirmDialog from "src/components/ConfirmDialog";
import type { EditTransactionProps } from "src/interfaces/adminHtmlState.interface";
import type { Transaction } from "src/interfaces";
import { getTransactionById } from "src/apiService/modules/transactions";
import { deleteTransactionsOptimistic } from "src/store/transaction/actions";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { bankAccountsSelector } from "src/store/bankAccount/selector/core";

const FetchingSpinnerDelayMS = 200;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "contents",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
}));

function EditTransactionDialog({ open }: { open: boolean }) {
  const classes = useStyles();
  const [openDelete, setOpenDelete] = useState(false);
  const [props, setProps] = useState<
    Omit<EditDialogProps, "onClose" | "onDelete">
  >({
    open: false,
  });
  const [showFetchBackdrop, setShowFetchBackdrop] = useState(false);
  const store = useStore();
  const dispatch = useDispatch();
  const currentActiveDialogProps = useSelector(
    activeDialogPropsSelector
  ) as EditTransactionProps;
  const id = open ? currentActiveDialogProps?.id : undefined;
  const requestId = open ? currentActiveDialogProps?.requestId : undefined;
  const requestIdRef = useRef(requestId);
  requestIdRef.current = requestId;

  useEffect(() => {
    let cancelled = false;
    let spinnerTimer: number | undefined;

    if (!open) {
      setProps((p) => ({ ...p, open: false }));
      setOpenDelete(false);
      setShowFetchBackdrop(false);
      return;
    }

    if (!id) return;

    const state = store.getState();
    const fromRedux = transactionByIdSelector(state, id);

    if (fromRedux?.read_only) {
      toast.warning("The transaction cannot be edited");
      dispatch(setActiveDialog(""));
      return;
    }

    if (fromRedux && !fromRedux.split_group_id) {
      setShowFetchBackdrop(false);
      setProps({
        open: true,
        data: { transaction: fromRedux },
      });
      return;
    }

    // Splits have their own dedicated dialog. If the clicked record is a split,
    // hand off to `edit_split` (it hydrates the group from Redux or fetches).
    if (fromRedux && fromRedux.split_group_id) {
      setShowFetchBackdrop(false);
      dispatch(
        setActiveDialog("edit_split", {
          split_group_id: fromRedux.split_group_id,
          id,
          requestId: requestIdRef.current,
        })
      );
      return;
    }

    setProps({ open: false });
    setShowFetchBackdrop(false);

    spinnerTimer = window.setTimeout(() => {
      if (!cancelled) setShowFetchBackdrop(true);
    }, FetchingSpinnerDelayMS);

    (async () => {
      try {
        const fetched = await getTransactionById(id);
        if (cancelled) return;
        if (!fetched) {
          setProps({ open: false });
          return;
        }
        const glAccounts = generalLedgerAccountsSelector(store.getState());
        const bankAccounts = bankAccountsSelector(store.getState());
        const account = fetched.gl_account_id
          ? glAccounts.find((a) => a.id === fetched.gl_account_id)
          : glAccounts.find((a) => a.name === fetched.account);
        const bank = fetched.bank_account_id
          ? bankAccounts.find((b) => b.id === fetched.bank_account_id)
          : bankAccounts.find((b) => b.name === fetched.integration_name);
        const transaction = {
          ...fetched,
          ...(account
            ? {
                gl_account_id: account.id,
                account: account.name,
                accountDoc: account,
              }
            : {}),
          ...(bank
            ? {
                bank_account_id: bank.id,
                integration_name: bank.name,
              }
            : {}),
        } as Transaction;
        if (cancelled) return;
        const latest = transactionByIdSelector(store.getState(), id);
        if (latest && !latest.split_group_id) {
          if (latest.read_only) {
            toast.warning("The transaction cannot be edited");
            dispatch(setActiveDialog(""));
            return;
          }
          setProps({
            open: true,
            data: { transaction: latest },
          });
          return;
        }
        if (transaction.read_only) {
          toast.warning("The transaction cannot be edited");
          dispatch(setActiveDialog(""));
          return;
        }
        if (transaction.split_group_id) {
          // Splits have their own dedicated dialog.
          setShowFetchBackdrop(false);
          dispatch(
            setActiveDialog("edit_split", {
              split_group_id: transaction.split_group_id,
              id,
              requestId: requestIdRef.current,
            })
          );
          return;
        }
        setProps({
          open: true,
          data: { transaction },
        });
      } finally {
        if (spinnerTimer !== undefined) {
          clearTimeout(spinnerTimer);
        }
        setShowFetchBackdrop(false);
      }
    })();

    return () => {
      cancelled = true;
      if (spinnerTimer !== undefined) {
        clearTimeout(spinnerTimer);
      }
      setShowFetchBackdrop(false);
    };
  }, [open, id, store, dispatch]);

  return (
    <div className={classes.root}>
      <Backdrop className={classes.backdrop} open={showFetchBackdrop && open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ConfirmDialog
        open={props.open && openDelete}
        title="Confirm"
        text="Are you sure you want to delete the transaction?"
        onCancel={() => {
          setOpenDelete(false);
        }}
        onConfirm={async () => {
          const transactionId = props.data?.transaction.id;
          if (transactionId) {
            await dispatch(deleteTransactionsOptimistic([transactionId]));
            toast.success("Transaction deleted successfully!");
          }

          setOpenDelete(false);
          dispatch(
            setActiveDialog("", {
              requestId: currentActiveDialogProps?.requestId,
              updated: !!props.data?.transaction.id,
            })
          );
        }}
      />
      <EditDialog
        {...props}
        onDelete={() => {
          setOpenDelete(true);
        }}
        onClose={(hasChanged?: boolean) => {
          dispatch(
            setActiveDialog("", {
              requestId: currentActiveDialogProps?.requestId,
              updated: !!hasChanged,
            })
          );
        }}
      />
    </div>
  );
}

export default EditTransactionDialog;
